import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { DiscoveryProgress } from './DiscoveryProgress';
import { serversApi, DiscoveryJobResponse } from '../../lib/api/servers';
import { useToastStore } from '../../stores/toastStore';
import { getErrorMessage } from '../../lib/api/client';

interface DiscoveryButtonProps {
  serverId: string;
  onComplete?: () => void;
}

export function DiscoveryButton({ serverId, onComplete }: DiscoveryButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [job, setJob] = useState<DiscoveryJobResponse | null>(null);
  const [starting, setStarting] = useState(false);
  const { addToast } = useToastStore();

  const handleTriggerDiscovery = async () => {
    setStarting(true);
    try {
      const response = await serversApi.triggerDiscovery(serverId, 'merge');
      setJob(response);
      setShowModal(true);
      addToast({
        type: 'success',
        message: 'Discovery job started successfully',
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setStarting(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setJob(null);
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <>
      <Button
        onClick={handleTriggerDiscovery}
        disabled={starting}
      >
        {starting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            <Search className="w-4 h-4 mr-2" />
            Auto-Discover Resources
          </>
        )}
      </Button>

      {showModal && job && (
        <Modal
          title="Auto-Discovery Progress"
          isOpen={showModal}
          onClose={handleClose}
        >
          <DiscoveryProgress
            serverId={serverId}
            jobId={job.job_id}
            onComplete={handleClose}
          />
        </Modal>
      )}
    </>
  );
}
